import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const CUSTOM_IMAGE_THUMB_STYLE_ID = 'cart-custom-image-thumbnail-style';

/**
 * Fetch the complete header section once and share the response with the
 * deferred menu and localization components.
 * @param {string} sectionId
 * @returns {Promise<string>}
 */
function requestHeaderSection(sectionId, { fresh = false } = {}) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);

  const globalWindow =
    /** @type {Window & { __cbnHeaderSectionRequests?: Map<string, Promise<string>> }} */ (window);
  const cache = (globalWindow.__cbnHeaderSectionRequests ||= new Map());
  const cacheKey = url.toString();

  if (fresh) cache.delete(cacheKey);

  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const request = fetch(cacheKey, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Header section request failed with status ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      cache.delete(cacheKey);
      throw error;
    });

  cache.set(cacheKey, request);
  return request;
}

function ensureCustomImageThumbnailStyles() {
  if (document.getElementById(CUSTOM_IMAGE_THUMB_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = CUSTOM_IMAGE_THUMB_STYLE_ID;
  style.textContent = `
    cart-drawer-component .cart-items__properties {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
    }

    cart-drawer-component .cart-items__properties img {
      width: auto !important;
      height: auto !important;
      max-width: 80px;
      max-height: 100px;
      object-fit: contain;
    }
  `;

  document.head.appendChild(style);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {Promise<void> | undefined} */
  #hydrationPromise;

  #hydrationVersion = 0;

  /**
   * Replace the lightweight drawer content with the complete server-rendered
   * cart. The visible loading state remains operable if the request is slow.
   * @returns {Promise<void>}
   */
  #hydrateDrawer(sectionHtml) {
    if (!this.hasAttribute('data-lazy-content')) return Promise.resolve();
    if (this.#hydrationPromise) return this.#hydrationPromise;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve();

    const version = this.#hydrationVersion;
    this.#hydrationPromise = (sectionHtml ? Promise.resolve(sectionHtml) : requestHeaderSection(sectionId))
      .then(async (html) => {
        if (version !== this.#hydrationVersion || !this.isConnected || !this.hasAttribute('data-lazy-content')) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const sourceComponent = parsed.querySelector(
          `cart-drawer-component[data-section-id="${CSS.escape(sectionId)}"]:not([data-lazy-content])`
        );
        const sourceDialog = sourceComponent?.querySelector('[ref="dialog"]');
        const sourceInner = sourceComponent?.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const targetDialog = this.refs.dialog;
        const targetInner = this.querySelector('[data-hydration-key="cart-drawer-inner"]');

        if (
          !(sourceDialog instanceof HTMLDialogElement) ||
          !(sourceInner instanceof HTMLElement) ||
          !(targetDialog instanceof HTMLDialogElement) ||
          !(targetInner instanceof HTMLElement)
        ) {
          throw new Error('Deferred cart drawer content was not found');
        }

        // Scripts parsed from fetched HTML are inert when their nodes are imported.
        // Load the original theme modules before inserting the deferred controls so
        // quantities, remove buttons, upsells and their native cart events are active.
        const moduleSources = Array.from(sourceInner.querySelectorAll('script[type="module"][src]'))
          .map((script) => script.getAttribute('src'))
          .filter(Boolean)
          .map((source) => new URL(source, window.location.href).href);

        await Promise.all([...new Set(moduleSources)].map((source) => import(source)));

        if (version !== this.#hydrationVersion || !this.isConnected) return;

        const fragment = document.createDocumentFragment();
        for (const child of sourceInner.children) {
          if (child.tagName === 'SCRIPT') continue;

          const importedChild = document.importNode(child, true);
          importedChild.querySelectorAll('script').forEach((script) => script.remove());
          fragment.append(importedChild);
        }

        targetInner.replaceChildren(fragment);
        targetDialog.className = sourceDialog.className;
        targetDialog.setAttribute('aria-labelledby', sourceDialog.getAttribute('aria-labelledby') || '');

        const sourceTrigger = sourceComponent.querySelector('[data-testid="cart-drawer-trigger"]');
        const targetTrigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
        if (sourceTrigger instanceof HTMLButtonElement && targetTrigger instanceof HTMLButtonElement) {
          const triggerFragment = document.createDocumentFragment();
          for (const child of sourceTrigger.childNodes) {
            triggerFragment.append(document.importNode(child, true));
          }
          targetTrigger.replaceChildren(triggerFragment);
          targetTrigger.setAttribute('aria-label', sourceTrigger.getAttribute('aria-label') || '');
          targetTrigger.setAttribute('aria-describedby', sourceTrigger.getAttribute('aria-describedby') || '');
        }

        for (const attribute of ['data-gift-variants', 'data-rewards-consistent', 'data-rewards-updating']) {
          const value = sourceComponent.getAttribute(attribute);
          if (value === null) this.removeAttribute(attribute);
          else this.setAttribute(attribute, value);
        }

        const sourceTemplate = sourceComponent.querySelector('#empty-cart-template');
        const targetTemplate = this.querySelector('#empty-cart-template');
        if (sourceTemplate instanceof HTMLTemplateElement && targetTemplate instanceof HTMLTemplateElement) {
          targetTemplate.innerHTML = sourceTemplate.innerHTML;
        }

        this.removeAttribute('data-lazy-content');
        this.updatedCallback();
        targetInner.removeAttribute('aria-busy');
      })
      .catch((error) => {
        if (version !== this.#hydrationVersion) return;
        this.#hydrationPromise = undefined;
        if (sectionHtml) return this.#refreshDrawer();
        this.querySelector('.cbn-cart-drawer-loading')?.setAttribute('aria-busy', 'false');
        console.warn('Crafty deferred cart drawer could not be loaded.', error);
      });

    return this.#hydrationPromise;
  }

  /**
   * Fetch an authoritative cart snapshot after a successful cart mutation.
   * Reusing the initial cached header response here would reopen stale content.
   * @returns {Promise<void>}
   */
  #refreshDrawer(sectionHtml) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve();

    this.setAttribute('data-lazy-content', '');
    this.querySelector('[data-hydration-key="cart-drawer-inner"]')?.setAttribute('aria-busy', 'true');
    this.#hydrationVersion += 1;
    this.#hydrationPromise = undefined;

    // Start and cache a fresh request so #hydrateDrawer reuses this promise
    // without issuing a second network request.
    if (!sectionHtml) requestHeaderSection(sectionId, { fresh: true });
    return this.#hydrateDrawer(sectionHtml);
  }

  connectedCallback() {
    super.connectedCallback();
    ensureCustomImageThumbnailStyles();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    if (this.hasAttribute('data-lazy-content')) {
      this.#refreshDrawer(event.detail?.data?.sections?.[this.dataset.sectionId]);
    }
  };

  open() {
    this.showDialog();
    this.#hydrateDrawer();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
